import { useTranslation } from 'react-i18next';

import { DynamicFormModal } from '@/shared/components/dynamic-form/DynamicFormModal/DynamicFormModal';
import { type ModalComponentProps } from '@/shared/components/Modals/modal-registry/modal-registry.types';
import { useToast } from '@/shared/hooks/useToast/useToast';
import { toApiError } from '@/shared/utils/transform-error/transform-error';

import {
  buildPublishFields,
  PublishFormSchema,
  type PublishFormValues,
} from '../../config/legal-publish-form.config';
import { usePublishVersionMutation, useVersionQuery } from '../../services/legal/legal.api';

/**
 * The irreversible act (N16, N27). The dialog exists to make the operator
 * choose the materiality with its consequence in front of them; nothing here
 * is preselected.
 */
export function PublishVersionModal({ open, onCancel, modalData }: ModalComponentProps) {
  const { t } = useTranslation('legal');
  const toast = useToast();

  const versionId = typeof modalData?.['versionId'] === 'string' ? modalData['versionId'] : '';
  const documentId = typeof modalData?.['documentId'] === 'string' ? modalData['documentId'] : '';

  const detail = useVersionQuery(versionId, { skip: versionId === '' });
  const [publishVersion] = usePublishVersionMutation();

  return (
    <DynamicFormModal<PublishFormValues>
      isOpen={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onCancel();
      }}
      title={t('publish.title', { version: detail.data?.version ?? '' })}
      description={t('publish.description')}
      size="lg"
      isLoading={detail.isLoading}
      fields={buildPublishFields(t)}
      schema={PublishFormSchema}
      initialValues={{ effectiveAt: null }}
      submitLabel={t('publish.confirm')}
      onCancel={onCancel}
      onSubmit={async (values) => {
        try {
          await publishVersion({
            id: versionId,
            documentId,
            body: {
              materiality: values.materiality,
              // An empty picker means "now", which the contract spells as absence.
              ...(values.effectiveAt === null || values.effectiveAt === undefined
                ? {}
                : { effectiveAt: values.effectiveAt }),
            },
          }).unwrap();
          toast.success(t(`publish.published.${values.materiality}`));
          onCancel();
        } catch (error) {
          throw toApiError(error);
        }
      }}
    />
  );
}
